import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes, FaTicketAlt } from "react-icons/fa";

const formatDateTime = (iso) => {
	if (!iso) return "—";
	return new Date(iso).toLocaleString("en-US", {
		month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
	});
};

const Badge = ({ label, colorMap }) => {
	const color = colorMap?.[label] || "#6b7280";
	return (
		<span style={{
			fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5,
			background: `${color}18`, color, border: `1px solid ${color}40`, whiteSpace: "nowrap",
		}}>
			{label || "—"}
		</span>
	);
};

const ReopenedTicketsDrawer = ({ open, onClose, tickets, totalReopenCount, priorityColors, statusColors }) => {
	useEffect(() => {
		if (!open) return;
		const handleEscape = (e) => { if (e.key === "Escape") onClose(); };
		document.addEventListener("keydown", handleEscape);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = previousOverflow;
		};
	}, [open, onClose]);

	const list = tickets || [];
	const hasMore = totalReopenCount > list.length;

	return (
		<AnimatePresence>
			{open && (
				<>
					<motion.div
						initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						onClick={onClose}
						style={{
							position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1040,
						}}
					/>
					<motion.div
						initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
						transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
						role="dialog" aria-modal="true" aria-label="Reopened tickets"
						style={{
							position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1050,
							width: "100%", maxWidth: 480,
							background: "var(--color-bg-card)", boxShadow: "-8px 0 30px rgba(0,0,0,0.2)",
							display: "flex", flexDirection: "column",
						}}
					>
						{/* ── Sticky header ─────────────────────────────────────────── */}
						<div style={{
							position: "sticky", top: 0, zIndex: 1,
							display: "flex", alignItems: "center", justifyContent: "space-between",
							padding: "16px 20px", borderBottom: "1px solid var(--color-border)",
							background: "var(--color-bg-card)", flexShrink: 0,
						}}>
							<div>
								<h6 className="fw-bold m-0" style={{ fontSize: 15, color: "var(--color-text)" }}>
									Reopened Tickets ({list.length})
								</h6>
								{hasMore && (
									<p className="m-0" style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 2 }}>
										Showing {list.length} of {totalReopenCount} reopen events in this period
									</p>
								)}
							</div>
							<button
								onClick={onClose}
								aria-label="Close"
								style={{
									display: "flex", alignItems: "center", justifyContent: "center",
									width: 30, height: 30, borderRadius: 8, border: "1px solid var(--color-border)",
									background: "transparent", color: "var(--color-text-light)", cursor: "pointer",
									flexShrink: 0,
								}}
								onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.4)"; }}
								onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--color-text-light)"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
							>
								<FaTimes size={13} />
							</button>
						</div>

						{/* ── Scrollable ticket list ───────────────────────────────────── */}
						<div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
							{!list.length ? (
								<div style={{
									display: "flex", flexDirection: "column", alignItems: "center",
									justifyContent: "center", minHeight: 200, gap: 8, color: "var(--color-text-light)",
								}}>
									<FaTicketAlt size={24} style={{ opacity: 0.25 }} />
									<span style={{ fontSize: 12 }}>No reopened tickets in this period.</span>
								</div>
							) : (
								<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
									{list.map(t => (
										<div key={t.id} style={{
											border: "1px solid var(--color-border)", borderRadius: 10,
											padding: 14, background: "var(--color-bg-card)",
										}}>
											<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
												<div style={{ minWidth: 0 }}>
													<div style={{ fontSize: 11, fontWeight: 700, color: "#ef4444" }}>{t.display_id}</div>
													<div style={{
														fontSize: 13, fontWeight: 600, color: "var(--color-text)",
														overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
													}}>
														{t.subject || "—"}
													</div>
												</div>
												<Badge label={t.priority_normalized} colorMap={priorityColors} />
											</div>

											<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
												<Badge label={t.status_normalized} colorMap={statusColors} />
												{t.category && (
													<span style={{ fontSize: 11, color: "var(--color-text-light)", padding: "2px 8px" }}>
														{t.category}
													</span>
												)}
											</div>

										<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 16px", fontSize: 11, color: "var(--color-text-light)" }}>
											{t.requester && <span><strong style={{ color: "var(--color-text)" }}>Requester:</strong> {t.requester}</span>}
											{t.group_name && <span><strong style={{ color: "var(--color-text)" }}>Group:</strong> {t.group_name}</span>}
											<span><strong style={{ color: "var(--color-text)" }}>Last reopened:</strong> {formatDateTime(t.reopened_at)}</span>
											{t.reopen_count > 1 && (
												<span style={{
													fontSize: 10, fontWeight: 700, color: "#ef4444",
													background: "rgba(239,68,68,0.1)", borderRadius: 4, padding: "1px 6px",
												}}>
													Reopened {t.reopen_count}×
												</span>
											)}
										</div>
										</div>
									))}
								</div>
							)}
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
};

export default ReopenedTicketsDrawer;
